import React, {useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {getSuperAdminDashboard} from '../../generic/crud'
import Chart from 'react-apexcharts'
import {CacheTimerBadge} from '../../dashboard/CacheTimerBadge'

const profileBreadCrumbs = [
    {title: 'Super Admin', path: '/sadashboard', isActive: false},
]

function getBillingStatusBadge(status) {
    switch (status) {
        case 'ACTIVE':
            return <span className="badge badge-light-success fs-8">Active</span>
        case 'WARNING':
            return <span className="badge badge-light-warning fs-8">Warning</span>
        case 'SUSPENDED':
            return <span className="badge badge-light-danger fs-8">Suspended</span>
        default:
            return <span className="badge badge-light-info fs-8">Demo</span>
    }
}

function SparklineChart({data}) {
    const categories = data.map(d => {
        const parts = d.date.split('-')
        return parts[2] + '/' + parts[1]
    })
    const values = data.map(d => d.count)

    const options = {
        chart: {
            type: 'bar',
            sparkline: {enabled: true},
            toolbar: {show: false},
        },
        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 2,
            },
        },
        colors: ['#009ef7'],
        tooltip: {
            enabled: true,
            x: {show: true},
            y: {
                title: {
                    formatter: () => 'Jobs:',
                },
            },
        },
        xaxis: {
            categories: categories,
        },
    }

    return (
        <Chart
            options={options}
            series={[{name: 'Job Cards', data: values}]}
            type="bar"
            height={35}
            width={120}
        />
    )
}

export function SADashboardPage() {
    const [data, setData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [refreshCounter, setRefreshCounter] = useState(0)
    const [countdown, setCountdown] = useState(0)
    const [cacheExpiry, setCacheExpiry] = useState(null)

    useEffect(() => {
        loadData()
    }, [refreshCounter])

    function loadData() {
        setIsLoading(true)
        getSuperAdminDashboard()
            .then(({data}) => {
                setData(data)
                setCacheExpiry(data.cacheExpiry || null)
                setIsLoading(false)
            })
            .catch((err) => {
                console.error('Error fetching SA dashboard:', err)
                setIsLoading(false)
            })
    }

    // Countdown timer for cache expiry
    useEffect(() => {
        if (!cacheExpiry) return

        const updateCountdown = () => {
            const now = new Date().getTime()
            const expiry = new Date(cacheExpiry).getTime()
            const remaining = Math.max(0, Math.floor((expiry - now) / 1000))
            setCountdown(remaining)

            if (remaining <= 0) {
                clearInterval(interval)
            }
        }

        updateCountdown()
        const interval = setInterval(updateCountdown, 1000)
        return () => clearInterval(interval)
    }, [cacheExpiry])

    function formatCountdown(seconds) {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    if (isLoading) {
        return (
            <>
                <PageTitle breadcrumbs={profileBreadCrumbs}>Dashboard</PageTitle>
                <div className="text-center py-20">
                    <span className="spinner-border text-primary" role="status"></span>
                    <p className="text-muted mt-3">Loading dashboard...</p>
                </div>
            </>
        )
    }

    if (!data) {
        return (
            <>
                <PageTitle breadcrumbs={profileBreadCrumbs}>Dashboard</PageTitle>
                <div className="text-center py-20">
                    <p className="text-muted">Failed to load dashboard data.</p>
                    <button className="btn btn-sm btn-light-primary" onClick={loadData}>
                        Retry
                    </button>
                </div>
            </>
        )
    }

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Dashboard</PageTitle>

            {/* Header with Refresh Button */}
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="text-gray-900 fw-bolder fs-1 mb-2">Super Admin Dashboard</h1>
                    <span className="text-muted fw-bold fs-6">
                        Organization overview and usage metrics
                    </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {cacheExpiry && countdown > 0 && (
                        <CacheTimerBadge countdown={countdown} formatCountdown={formatCountdown} />
                    )}
                    <button
                        className="btn btn-sm btn-light-primary"
                        onClick={() => setRefreshCounter(prev => prev + 1)}
                        disabled={isLoading}
                    >
                        <i className={`bi bi-arrow-clockwise me-2 ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Summary KPI Cards */}
            <div className="row g-5 g-xl-8 mb-8">
                <div className="col-xl-4">
                    <div className="card card-flush h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="symbol symbol-50px bg-light-primary me-4">
                                <span className="symbol-label">
                                    <i className="bi bi-building fs-2 text-primary"></i>
                                </span>
                            </div>
                            <div>
                                <div className="fs-2hx fw-bold text-dark">{data.totalOrganizations}</div>
                                <div className="fw-semibold text-muted fs-6">Total Organizations</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="card card-flush h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="symbol symbol-50px bg-light-success me-4">
                                <span className="symbol-label">
                                    <i className="bi bi-shop fs-2 text-success"></i>
                                </span>
                            </div>
                            <div>
                                <div className="fs-2hx fw-bold text-dark">{data.totalBranches}</div>
                                <div className="fw-semibold text-muted fs-6">Total Branches</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="card card-flush h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="symbol symbol-50px bg-light-warning me-4">
                                <span className="symbol-label">
                                    <i className="bi bi-clipboard-check fs-2 text-warning"></i>
                                </span>
                            </div>
                            <div>
                                <div className="fs-2hx fw-bold text-dark">{data.totalJobCardsLast7Days}</div>
                                <div className="fw-semibold text-muted fs-6">Job Cards (Last 7 Days)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Organization Table */}
            <div className="card card-flush">
                <div className="card-header border-0 pt-5">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3">Organization Usage</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">Last 7 days activity</span>
                    </h3>
                </div>
                <div className="card-body pt-3">
                    <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                            <thead>
                                <tr className="fw-bold text-muted">
                                    <th className="min-w-200px">Organization</th>
                                    <th className="min-w-100px">Status</th>
                                    <th className="min-w-80px text-center">Branches</th>
                                    <th className="min-w-120px text-center">Job Cards (7d)</th>
                                    <th className="min-w-150px text-center">Daily Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.organizations.map((org) => (
                                    <tr key={org.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="symbol symbol-35px bg-light-primary me-3">
                                                    <span className="symbol-label fw-bold text-primary fs-7">
                                                        {org.name ? org.name.charAt(0).toUpperCase() : '?'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-dark fw-bold d-block fs-6">{org.name}</span>
                                                    {!org.active && (
                                                        <span className="text-muted fw-semibold fs-8">Inactive</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{getBillingStatusBadge(org.billingStatus)}</td>
                                        <td className="text-center">
                                            <span className="fw-bold fs-6">{org.branchCount}</span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`fw-bold fs-6 ${org.totalJobCardsLast7Days > 0 ? 'text-success' : 'text-muted'}`}>
                                                {org.totalJobCardsLast7Days}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {org.jobCardsByDay && org.jobCardsByDay.length > 0 ? (
                                                <div className="d-flex justify-content-center">
                                                    <SparklineChart data={org.jobCardsByDay} />
                                                </div>
                                            ) : (
                                                <span className="text-muted fs-8">No data</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {data.organizations.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted py-10">
                                            No organizations found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    )
}
